import { asteroidClass, bigAsteroidChance } from './constants';
import type { Level } from './Level';

// Owns the gameplay asteroid group: spawning the rocks and the response when a
// projectile (a player laser or an enemy bolt) strikes one. The asteroid
// collision POLYGONS and the asteroid-vs-asteroid bounce live on the shared
// SpaceScene base (the menu backdrops reuse them), so this class reaches those
// through `level`.
export class Asteroids {
    group: Phaser.Physics.Arcade.Group;

    constructor(private level: Level) {
        this.group = level.physics.add.group({
            classType: Phaser.Physics.Arcade.Image,
            runChildUpdate: true
        });

        // Asteroid-vs-asteroid, resolved against the real polygon shapes
        // (SpaceScene.asteroidBounce, via SAT). Set up once as a persistent
        // overlap so the rocks keep bouncing even after the ship is destroyed.
        level.physics.add.overlap(this.group, this.group, level.asteroidBounce, null, level);
    }

    spawn(x: number, y: number): Phaser.Physics.Arcade.Image {
        const rnd = Phaser.Math.RND.frac();
        const asteroidSize = rnd <= bigAsteroidChance ? 1 : 0;
        const variant = asteroidSize == 1 ? "" : Phaser.Math.RND.pick(['a', 'b']);
        const asteroid = this.group.create(x, y, 'asteroid' + asteroidSize + variant) as Phaser.Physics.Arcade.Image;

        asteroid.setOrigin(0.5, 0.5);

        const angularVelocity = Phaser.Math.Between(
            asteroidClass[asteroidSize].MinAngularVelocity,
            asteroidClass[asteroidSize].MaxAngularVelocity
        );
        asteroid.setAngularVelocity(angularVelocity);

        asteroid.setData('mass', asteroidClass[asteroidSize].Mass);
        asteroid.setData('type', 'asteroid' + asteroidSize + variant);

        const randomAngle = Phaser.Math.DegToRad(Phaser.Math.Between(0, 360));
        const randomVelocity = Phaser.Math.Between(
            asteroidClass[asteroidSize].MinVelocity,
            asteroidClass[asteroidSize].MaxVelocity
        );

        this.level.physics.velocityFromRotation(randomAngle, randomVelocity, asteroid.body.velocity);

        asteroid.setCollideWorldBounds(true);
        asteroid.setBounce(1, 1);

        return asteroid;
    }

    // Process callback for a projectile/asteroid overlap: true only when the shot
    // actually touches the asteroid's polygon, not just its bounding box. Used
    // for both player lasers and enemy fire (both are LaserImages).
    laserHitsAsteroid(laser: Phaser.Physics.Arcade.Image, asteroid: Phaser.Physics.Arcade.Image): boolean {
        return this.level.shotHitsPolygon(laser, this.level.getAsteroidPolygon(asteroid));
    }

    // A projectile struck the asteroid: nudge the rock (mass-weighted) and remove
    // the projectile from play.
    collision(target: Phaser.Physics.Arcade.Image, asteroid: Phaser.Physics.Arcade.Image) {
        const mass = asteroid.getData('mass') || 1; // Default to 1 if mass is not set

        asteroid.body.velocity.x += target.body.velocity.x * (0.15 / mass);
        asteroid.body.velocity.y += target.body.velocity.y * (0.15 / mass);

        this.level.removeLaser(target as any);
    }
}
